import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
} from '@mui/material'
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material'
import { Company } from '../../../../core/models/Company'
import { walletService, CompanyWallet, WalletTransaction, WalletStats, TopUpRequest, AutoTopUpConfig } from '../../../../services/WalletService'
import { billingService, PaymentMethod } from '../../../../services/BillingService'
import { StyledButton, StyledIconButton } from '../../styles/StyledComponents'
import { TopUpDialog } from './TopUpDialog'
import { AutoTopUpDialog } from './AutoTopUpDialog'
import { alpha, useTheme } from '@mui/material/styles'

interface WalletManagementPanelProps {
  company: Company
  onBack?: () => void
}

export const WalletManagementPanel: React.FC<WalletManagementPanelProps> = ({
  company,
  onBack,
}) => {
  const theme = useTheme()
  const [wallet, setWallet] = React.useState<CompanyWallet | null>(null)
  const [stats, setStats] = React.useState<WalletStats | null>(null)
  const [transactions, setTransactions] = React.useState<WalletTransaction[]>([])
  const [loading, setLoading] = React.useState(true)
  const [initializing, setInitializing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showTopUpDialog, setShowTopUpDialog] = React.useState(false)
  const [showAutoTopUpDialog, setShowAutoTopUpDialog] = React.useState(false)
  const [showTransactionHistory, setShowTransactionHistory] = React.useState(false)
  const [transactionPage, setTransactionPage] = React.useState(1)
  const [transactionTotal, setTransactionTotal] = React.useState(0)
  const [refreshing, setRefreshing] = React.useState(false)
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] = React.useState(false)

  const transactionsPerPage = 10

  // Check if payment methods are available (only count active payment methods)
  const hasPaymentMethods = paymentMethods.filter(pm => pm.status === 'active').length > 0

  // Load payment methods
  const loadPaymentMethods = React.useCallback(async () => {
    try {
      setLoadingPaymentMethods(true)
      const result = await billingService.getCompanyPaymentMethods(company.id)
      
      if (result.success && result.payment_methods) {
        setPaymentMethods(result.payment_methods)
      } else {
        setPaymentMethods([])
      }
    } catch (error) {
      console.error('Error loading payment methods:', error)
      setPaymentMethods([])
    } finally {
      setLoadingPaymentMethods(false)
    }
  }, [company.id])

  // Load wallet data
  const loadWalletData = React.useCallback(async () => {
    try {
      setLoading(true)
      setInitializing(false)
      setError(null)

      // Load wallet, stats, and payment methods in parallel
      const [walletResult, statsResult] = await Promise.all([
        walletService.getWallet(company.id),
        walletService.getWalletStats(company.id),
        loadPaymentMethods()
      ])

      if (walletResult.success && walletResult.wallet) {
        setWallet(walletResult.wallet)
        // If wallet was just created, show initialization message briefly
        if (walletResult.initializing) {
          setInitializing(true)
          setTimeout(() => setInitializing(false), 2000)
        }
      } else {
        setError(walletResult.error || 'Failed to load wallet')
      }

      if (statsResult.success && statsResult.stats) {
        setStats(statsResult.stats)
      }

      // Attempt to refresh any pending transactions in the first page
      try {
        const result = await walletService.getTransactions(company.id, transactionsPerPage, 0)
        if (result.success && result.transactions) {
          const pending = result.transactions.filter(t => t.status !== 'completed')
          for (const t of pending) {
            await walletService.refreshTransaction(company.id, t.id)
          }
          if (showTransactionHistory) {
            await loadTransactions(transactionPage)
          }
        }
      } catch (e) {
        // ignore refresh errors
      }

    } catch (error: any) {
      console.error('Error loading wallet data:', error)
      setError('Failed to load wallet data')
    } finally {
      setLoading(false)
    }
  }, [company.id])

  // Load transaction history
  const loadTransactions = React.useCallback(async (page: number = 1) => {
    try {
      const offset = (page - 1) * transactionsPerPage
      const result = await walletService.getTransactions(company.id, transactionsPerPage, offset)
      
      if (result.success && result.transactions) {
        setTransactions(result.transactions)
        setTransactionTotal(result.total || 0)
        setTransactionPage(page)
      }
    } catch (error: any) {
      console.error('Error loading transactions:', error)
    }
  }, [company.id])

  // Load data on mount
  React.useEffect(() => {
    loadWalletData()
  }, [loadWalletData])

  // Listen for payment method updates from other components
  React.useEffect(() => {
    const handlePaymentMethodsUpdated = () => {
      console.log('Payment methods updated, refreshing wallet data...')
      loadPaymentMethods()
    }

    window.addEventListener('paymentMethodsUpdated', handlePaymentMethodsUpdated)
    
    return () => {
      window.removeEventListener('paymentMethodsUpdated', handlePaymentMethodsUpdated)
    }
  }, [loadPaymentMethods])

  // Handle top-up success
  const handleTopUpSuccess = () => {
    setShowTopUpDialog(false)
    loadWalletData()
    if (showTransactionHistory) {
      loadTransactions(1)
    }
  }

  // Handle auto top-up configuration
  const handleAutoTopUpSuccess = () => {
    setShowAutoTopUpDialog(false)
    loadWalletData()
  }

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true)
    await loadWalletData()
    if (showTransactionHistory) {
      await loadTransactions(transactionPage)
    }
    // Background refresh pending transactions for current page
    try {
      const offset = (transactionPage - 1) * transactionsPerPage
      const result = await walletService.getTransactions(company.id, transactionsPerPage, offset)
      if (result.success && result.transactions) {
        const pending = result.transactions.filter(t => t.status !== 'completed')
        for (const t of pending) {
          await walletService.refreshTransaction(company.id, t.id)
        }
        if (showTransactionHistory) {
          await loadTransactions(transactionPage)
        }
      }
    } catch (e) {
      // ignore
    }
    setRefreshing(false)
  }

  // Handle transaction history toggle
  const handleToggleTransactionHistory = () => {
    if (!showTransactionHistory) {
      (async () => {
        await loadTransactions(1)
        try {
          const result = await walletService.getTransactions(company.id, transactionsPerPage, 0)
          if (result.success && result.transactions) {
            const pending = result.transactions.filter(t => t.status !== 'completed')
            for (const t of pending) {
              await walletService.refreshTransaction(company.id, t.id)
            }
            await loadTransactions(1)
          }
        } catch (e) {
          // ignore
        }
      })()
    }
    setShowTransactionHistory(!showTransactionHistory)
  }

  // Handle transaction page change
  const handleTransactionPageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    loadTransactions(page)
  }

  const walletTxnStatusChipSx = (status: string) => {
    const s = status?.toLowerCase() || ''
    const main =
      s === 'completed'
        ? theme.palette.success.main
        : s === 'pending'
          ? theme.palette.warning.main
          : s === 'failed'
            ? theme.palette.error.main
            : theme.palette.text.secondary
    return {
      bgcolor: alpha(main, 0.2),
      backgroundImage: 'none',
      color: main,
      fontWeight: 500,
      fontSize: '0.7rem',
      height: '18px',
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
          {initializing ? 'Initializing wallet...' : 'Loading wallet data...'}
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <StyledButton onClick={loadWalletData} startIcon={<RefreshIcon />}>
          Retry
        </StyledButton>
      </Box>
    )
  }

  // This should not happen anymore since we auto-create wallets
  if (!wallet) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Unexpected error: Wallet data not available. Please refresh the page.
        </Alert>
        <StyledButton onClick={loadWalletData} startIcon={<RefreshIcon />}>
          Retry
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        pb: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {onBack && (
            <StyledIconButton
              onClick={onBack}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                p: 0.5,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  backgroundImage: 'none',
                },
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 16 }} />
            </StyledIconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                p: 0.5,
                borderRadius: 1,
                background: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 14 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0, lineHeight: 1.2 }}>
                Wallet Management
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {company.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <StyledIconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              p: 0.5,
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                backgroundImage: 'none',
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </StyledIconButton>
          <StyledButton
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowTopUpDialog(true)}
            disabled={!hasPaymentMethods}
            size="small"
            sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            title={!hasPaymentMethods ? 'Add a payment method first to enable wallet top-up' : 'Top up wallet balance'}
          >
            Top Up
          </StyledButton>
        </Box>
      </Box>

      {/* Wallet Overview */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Current Balance */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'action.hover',
                backgroundImage: 'none',
                borderColor: alpha(theme.palette.primary.main, 0.35),
                boxShadow: theme.shadows[4],
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  mr: 1,
                }}
              >
                <AccountBalanceIcon sx={{ color: 'primary.main', fontSize: 14 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                  Current Balance
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Available funds
                </Typography>
              </Box>
            </Box>
            <Typography variant="h4" sx={{ color: 'info.main', fontWeight: 700, mb: 0.75, fontSize: '1.75rem' }}>
              {wallet ? walletService.formatCurrency(wallet.balance, wallet.currency) : '$0.00'}
            </Typography>
            <Chip
              label={wallet?.status || 'Unknown'}
              size="small"
              sx={{
                bgcolor:
                  wallet?.status === 'active'
                    ? alpha(theme.palette.success.main, 0.2)
                    : 'action.hover',
                backgroundImage: 'none',
                color: wallet?.status === 'active' ? 'success.main' : 'text.secondary',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: '20px'
              }}
            />
          </Paper>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 24, mb: 0.5 }} />
                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600, fontSize: '1.1rem' }}>
                  {stats?.total_top_ups || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Top Ups
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <TrendingDownIcon sx={{ color: 'warning.main', fontSize: 24, mb: 0.5 }} />
                <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '1.1rem' }}>
                  {stats?.total_deductions || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Deductions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <HistoryIcon sx={{ color: 'info.main', fontSize: 24, mb: 0.5 }} />
                <Typography variant="h6" sx={{ color: 'info.main', fontWeight: 600, fontSize: '1.1rem' }}>
                  {stats?.total_transactions || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Transactions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                <AutoAwesomeIcon sx={{ color: 'secondary.main', fontSize: 24, mb: 0.5 }} />
                <Typography variant="h6" sx={{ color: 'secondary.main', fontWeight: 600, fontSize: '1.1rem' }}>
                  {wallet?.auto_top_up_enabled ? 'ON' : 'OFF'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Auto Top Up
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          transition: 'all 0.2s ease',
          mb: 2,
          '&:hover': {
            bgcolor: 'action.hover',
            backgroundImage: 'none',
            borderColor: alpha(theme.palette.primary.main, 0.35),
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              p: 0.5,
              borderRadius: 1,
              background: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              mr: 1,
            }}
          >
            <SettingsIcon sx={{ color: 'primary.main', fontSize: 14 }} />
          </Box>
          <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
            Quick Actions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <StyledButton
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowTopUpDialog(true)}
            disabled={!hasPaymentMethods}
            size="small"
            sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            title={!hasPaymentMethods ? 'Add a payment method first to enable wallet top-up' : 'Top up wallet balance'}
          >
            Top Up Wallet
          </StyledButton>
          <StyledButton
            variant="outlined"
            startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
            onClick={() => setShowAutoTopUpDialog(true)}
            size="small"
            sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
          >
            Configure Auto Top Up
          </StyledButton>
          <StyledButton
            variant="outlined"
            startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
            onClick={handleToggleTransactionHistory}
            size="small"
            sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
          >
            {showTransactionHistory ? 'Hide' : 'Show'} Transaction History
          </StyledButton>
        </Box>
      </Paper>

      {/* Wallet Initialization Success Message */}
      {initializing && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            bgcolor: alpha(theme.palette.success.main, 0.08),
            backgroundImage: 'none',
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            color: 'text.primary',
            '& .MuiAlert-icon': {
              color: 'success.main'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>Wallet Initialized:</strong> Your company wallet has been successfully created and is ready to use.
          </Typography>
        </Alert>
      )}

      {/* Payment Method Warning */}
      {!hasPaymentMethods && !loadingPaymentMethods && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            backgroundImage: 'none',
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            color: 'text.primary',
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>Payment Method Required:</strong> Add a payment method in the Billing & Tiers tab to enable wallet top-up functionality.
          </Typography>
        </Alert>
      )}

      {/* Transaction History */}
      {showTransactionHistory && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              backgroundImage: 'none',
              borderColor: alpha(theme.palette.primary.main, 0.35),
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  mr: 1,
                }}
              >
                <HistoryIcon sx={{ color: 'primary.main', fontSize: 14 }} />
              </Box>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                Transaction History
              </Typography>
            </Box>
            
            <TableContainer component={Paper} sx={{ 
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.35)
              }
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Balance
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontWeight: 600, paddingLeft: '12px', paddingTop: '8px', paddingBottom: '8px', borderColor: 'divider', fontSize: '0.75rem' }}>
                      Description
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell sx={{ color: 'text.secondary', paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider', fontSize: '0.75rem' }}>
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider', fontSize: '0.75rem' }}>
                        {walletService.formatTransactionType(transaction.transaction_type)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: transaction.transaction_type === 'top_up' || transaction.transaction_type === 'refund' 
                              ? 'success.main' 
                              : transaction.transaction_type === 'deduction' 
                                ? 'warning.main' 
                                : 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.75rem'
                          }}
                        >
                          {transaction.transaction_type === 'deduction' ? '-' : '+'}
                          {walletService.formatCurrency(transaction.amount, transaction.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider', fontSize: '0.75rem' }}>
                        {walletService.formatCurrency(transaction.balance_after, transaction.currency)}
                      </TableCell>
                      <TableCell sx={{ paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider' }}>
                        <Chip
                          label={transaction.status}
                          size="small"
                          sx={walletTxnStatusChipSx(transaction.status)}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', paddingLeft: '12px', paddingTop: '6px', paddingBottom: '6px', borderColor: 'divider', fontSize: '0.75rem' }}>
                        {transaction.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Quick row actions */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {transactions.some(t => t.status !== 'completed') && (
                <StyledButton
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    try {
                      const pending = transactions.filter(t => t.status !== 'completed')
                      for (const t of pending) {
                        await walletService.refreshTransaction(company.id, t.id)
                      }
                      await loadTransactions(transactionPage)
                    } catch (e) {
                      console.error('Error refreshing transactions:', e)
                    }
                  }}
                >
                  Refresh Pending Transactions
                </StyledButton>
              )}
            </Box>

            {transactionTotal > transactionsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(transactionTotal / transactionsPerPage)}
                  page={transactionPage}
                  onChange={handleTransactionPageChange}
                  color="primary"
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'text.primary',
                      fontSize: '0.75rem',
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: 'primary.main',
                      backgroundImage: 'none',
                      color: 'primary.contrastText',
                    },
                    '& .MuiPaginationItem-root:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Dialogs */}
      <TopUpDialog
        open={showTopUpDialog}
        onClose={() => setShowTopUpDialog(false)}
        onSuccess={handleTopUpSuccess}
        companyId={company.id}
        currentBalance={wallet?.balance || 0}
        disabled={!hasPaymentMethods}
      />

      <AutoTopUpDialog
        open={showAutoTopUpDialog}
        onClose={() => setShowAutoTopUpDialog(false)}
        onSuccess={handleAutoTopUpSuccess}
        companyId={company.id}
        currentConfig={{
          enabled: wallet?.auto_top_up_enabled || false,
          threshold: wallet?.auto_top_up_threshold || 10,
          amount: wallet?.auto_top_up_amount || 50
        }}
      />
    </Box>
  )
}
